/*
 * Reduces the commerce-api navigation payload to the payout-only mode set
 * (LAU-52).
 *
 * commerce-api is deliberately not modified for the Spring runout -- it is a
 * separate repo with its own deploy, it cannot read the payout_only_mode flag,
 * and its payload has consumers beyond this project. So both clients that
 * render this navigation filter it after fetching instead.
 *
 * DUPLICATE: rails-teespring applies the same allowlist and the same two
 * injected items in
 *   apps/seller/app/assets/javascripts/seller/dashboard_v2_nav/src/utils/payoutOnlyNav.js
 * The two copies must agree on the item set and its order. Sharing them would
 * mean cutting a @teespring/ts-components release, which is not worth it before
 * the cutover date. If you change this file, change its twin in the same breath.
 *
 * The account menu (user.navLinks) is reduced here too. /api/v2/user/header
 * returns no navLinks at all -- it is Commerce::Api::V2::HeaderPayload, not the
 * Core header endpoint -- so the commerce-api list is what survives the merge
 * and what a creator actually sees.
 *
 * See openspec/changes/payout-only-mode/specs/dashboard-navigation/spec.md.
 */

export const HELP_CENTER_URL = 'https://kb.spri.ng/creators';
export const DOMAINS_URL = 'https://account.teespring.com';

// Labels kept from the upstream payload. Everything else is dropped, including
// Learn -- ts-components renders that as a hardcoded support flyout keyed off
// locationPath 'learn', so it cannot be relabelled into a plain link.
const KEPT_LABELS = ['Home', 'Payouts', 'Settings'];

// Top to bottom, as specified.
const ORDER = ['Home', 'Payouts', 'Domains', 'Settings', 'Help Center'];

// Dropped as soon as creation is disabled (LAU-141, LAU-142), while the rest of
// the navigation is still intact.
const CREATION_DISABLED_DROPS = ['Create', 'Upgrades'];

const injectedItems = () => [
  {
    icon: 'ExternalLink',
    label: 'Domains',
    locationUrl: DOMAINS_URL,
    locationPath: DOMAINS_URL,
    dashboard: 1
  },
  {
    icon: 'HelpCircle',
    label: 'Help Center',
    locationUrl: HELP_CENTER_URL,
    locationPath: HELP_CENTER_URL,
    dashboard: 1
  }
];

// The account menu is reduced to Sign out: the Dashboard entry duplicates
// destinations already in the main navigation, and the policies link is covered
// by Help Center.
//
// Matched on destination, not label. Labels differ between environments -- the
// policies entry is "Policies" in the deployed payload and "Spring policies" in
// the commerce-api source -- so label matching silently failed to drop it.
// URLs are stable across environments in a way the copy is not.
//
// Still a drop-list rather than a keep-list. A keep-list would leave a creator
// no way to sign out if upstream changed the logout entry; dropping named
// destinations means Sign out cannot be lost, and anything unrecognised merely
// survives.
const DROPPED_ACCOUNT_MENU_PATTERNS = [
  /\/policies(\/|$)/i,
  /\/overview(\/|\?|$)/i
];

const DROPPED_ACCOUNT_MENU_LABELS = [
  'Dashboard',
  'Policies',
  'Spring policies'
];

const isDroppedAccountMenuEntry = (link) => {
  const url = String(link?.locationUrl ?? '');

  return (
    DROPPED_ACCOUNT_MENU_LABELS.includes(link?.label) ||
    DROPPED_ACCOUNT_MENU_PATTERNS.some((pattern) => pattern.test(url))
  );
};

const reduceAccountMenu = (navLinks) =>
  (navLinks ?? []).filter((link) => !isDroppedAccountMenuEntry(link));

const applyPayoutOnly = (header) => {
  const upstream = header?.items ?? [];

  const kept = upstream
    .filter((item) => KEPT_LABELS.includes(item.label))
    .map((item) => ({ ...item, subNav: [] }));

  // An upstream payload we recognise nothing in means the allowlist has gone
  // stale. Surface it rather than quietly rendering a two-item nav that offers
  // no way back to Payouts.
  if (kept.length === 0) {
    /* eslint-disable-next-line no-console */
    console.error(
      'payout-only mode: no navigation items matched the allowlist',
      upstream.map((item) => item.label)
    );
  }

  const byLabel = {};
  [...kept, ...injectedItems()].forEach((item) => {
    byLabel[item.label] = item;
  });

  const items = ORDER.map((label) => byLabel[label]).filter(Boolean);
  const labels = items.map((item) => item.label);

  return {
    ...header,
    items,
    user: {
      ...header?.user,
      navLinks: reduceAccountMenu(header?.user?.navLinks)
    },
    // The mobile featured row maps labels back to items, so an entry that no
    // longer resolves would render as a blank.
    featuredNavLabels: (header?.featuredNavLabels ?? []).filter((label) =>
      labels.includes(label)
    )
  };
};

// Only Create and Upgrades go at the first step; everything else stays.
const applyCreationDisabled = (header) => {
  const items = (header?.items ?? []).filter(
    (item) => !CREATION_DISABLED_DROPS.includes(item.label)
  );
  const labels = items.map((item) => item.label);

  return {
    ...header,
    items,
    featuredNavLabels: (header?.featuredNavLabels ?? []).filter((label) =>
      labels.includes(label)
    )
  };
};

// Payout-only mode is a superset of creation disabled, so it wins outright.
export const applyNavReduction = (
  header,
  { payoutOnlyMode, creationDisabled }
) => {
  if (payoutOnlyMode) return applyPayoutOnly(header);
  if (creationDisabled) return applyCreationDisabled(header);

  return header;
};

export default applyNavReduction;
